import Button from '@/common/components/Button'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import { DateInput } from '@/common/components/DateInput/DateInput'
import { ErrorMessage } from '@/common/components/ErrorMessage'
import { FileUploader } from '@/common/components/FileUploader'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import { Mapper } from '@/common/services/mapper'
import { FormTransformers } from '@/common/utils/formTransformers'
import { PaymentModalityType } from '@/features/companies/types/companies.type'
import { companyGovernmentAllocationDocumentRequiredOptions } from '@/features/privateCompanies/constants/companyGovernmentAllocationDocumentRequired.constant'
import { useCreateGovernmentAllocation } from '@/features/privateCompanies/hooks/useCreateGovernmentAllocation'
import {
  createGovernmentAllocationSchema,
  CreateGovernmentAllocationValidationSchema,
} from '@/features/privateCompanies/schemas/createGovernmentAllocation.schema'
import { CompanyBranchData } from '@/features/privateCompanies/types/companyBranches.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useAvailableCompanyBranches } from '../../hooks/companyBranches/useAvailableCompanyBranches'
import { governmentAllocationMovementsKindOptions } from '../../constants/governmentAllocations/governmentAllocationMovementsKind.constant'

export const CreateGovernmentAllocationModal = ({
  companyPaymentModality,
}: {
  companyPaymentModality?: PaymentModalityType
}) => {
  const { id: companyId } = useParams()
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [searchCompanyBranch, setSearchCompanyBranch] = useDebounce('');

  const { data: companyBranchOptions } = useAvailableCompanyBranches({
    companyId,
    filters: {
      search: searchCompanyBranch,
    },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const {
    control,
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    watch,
    reset: resetForm,
    formState: { errors },
  } = useForm<CreateGovernmentAllocationValidationSchema>({
    resolver: zodResolver(createGovernmentAllocationSchema),
    defaultValues: {
      file_required: companyGovernmentAllocationDocumentRequiredOptions.find((option) => option.value === 'attach_document'),
      movements_kind: governmentAllocationMovementsKindOptions.find((option) => option.value === 'common'),
    }
  })

  const file = watch('file')

  const onSubmit: SubmitHandler<CreateGovernmentAllocationValidationSchema> = (form) => {
    createGovernmentAllocation({ form })
  }

  const onSuccess = () => {
    resetForm()
    setValue('total_value', '')
    queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocations'] })
    setIsOpen(false)
  }

  const { mutate: createGovernmentAllocation, isPending: createGovernmentAllocationIsPending } =
    useCreateGovernmentAllocation({
      onSuccess,
      onError: (error) => {
        FormTransformers.errorsTransformer({ setError, setMessages })(error);
      },
    })

  useEffect(() => {
    if (companyPaymentModality === 'pre_paid') {
      setValue(
        'file_required',
        companyGovernmentAllocationDocumentRequiredOptions.find((option) => option.value === 'attach_document')!,
      )
    } else {
      setValue(
        'file_required',
        companyGovernmentAllocationDocumentRequiredOptions.find((option) => option.value === 'force_post_paid')!,
      )
    }
  }, [isOpen, companyPaymentModality])

  useEffect(() => {
    resetForm();
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonIsLoading={createGovernmentAllocationIsPending}
      disabled={createGovernmentAllocationIsPending}
      Trigger={
        <Button operationKlassToCheckPermission='Admin::Billing::GovernmentAllocations::Create' className="rounded-sm shadow-[0px_0px_8px_4px_rgba(0,_0,_0,_0.2)]">
          {companyPaymentModality === 'pre_paid' ? 'Novo empenho' : 'Nova cota estimativa'}
        </Button>
      }
      title={`Cadastrando ${companyPaymentModality === 'pre_paid' ? 'empenho' : 'cota estimativa'}`}
      footer={`${createGovernmentAllocationIsPending ? 'Cadastrando' : 'Cadastrar'} ${companyPaymentModality === 'pre_paid' ? 'empenho' : 'cota estimativa'}`}
      body={
        <form className="flex flex-col gap-4 text-white-800">
          <SelectController
            control={control}
            name="branch_id"
            label="Filial"
            onInputChange={setSearchCompanyBranch}
            markAsRequired
            options={companyBranchOptions || []}
            errorMessage={errors.branch_id?.message}
            isDisabled={createGovernmentAllocationIsPending}
          />

          <div className="space-y-2">
            <p className="text-[11px] leading-3">Período de competência</p>

            <div className="flex items-start gap-x-2">
              <DateInput
                fieldName="start_of_document_validity"
                control={control}
                errors={errors}
                label="Início"
                markAsRequired
                className="w-1/2"
                disabled={createGovernmentAllocationIsPending}
              />

              <DateInput
                fieldName="end_of_document_validity"
                control={control}
                errors={errors}
                label="Fim"
                markAsRequired
                className="w-1/2"
                disabled={createGovernmentAllocationIsPending}
              />
            </div>
          </div>

          <TextField
            label="Número do ofício do empenho"
            markAsRequired
            maxLength={24}
            errorMessage={errors?.office_number?.message}
            {...register('office_number')}
            disabled={createGovernmentAllocationIsPending}
          />

          <CurrencyTextField
            label={`Valor total ${companyPaymentModality === 'pre_paid' ? 'do empenho' : 'da cota estimativa'}`}
            markAsRequired
            maxValue={1_000_000_000}
            errorMessage={errors.total_value?.message}
            {...register('total_value')}
            disabled={createGovernmentAllocationIsPending}
          />

          <SelectController
            control={control}
            name="movements_kind"
            label="Tipo de movimentação"
            markAsRequired
            options={governmentAllocationMovementsKindOptions}
            errorMessage={errors.movements_kind?.message}
          />

          {companyPaymentModality === 'pre_paid' && (
            <>
              <SelectController
                name="file_required"
                control={control}
                label="Solicitação de documento"
                markAsRequired
                options={companyGovernmentAllocationDocumentRequiredOptions}
                isDisabled={createGovernmentAllocationIsPending}
              />

              <div>
                <FileUploader
                  name="file"
                  accept="application/pdf, image/png, image/jpg, image/jpeg"
                  title="Anexe o Empenho"
                  className="w-full bg-yellow-op-2 border-none"
                  fileName={file ? file.name : undefined}
                  currentFileUrl={file ? URL.createObjectURL(file) : undefined}
                  handleChange={(file) => {
                    clearErrors('file')
                    setValue('file', file)
                  }}
                  disabled={createGovernmentAllocationIsPending}
                />

                {errors.file?.message && (
                  <ErrorMessage
                    name="file"
                    message={errors.file?.message}
                  />
                )}
              </div>
            </>
          )}

          {messages && (
            <ModalMessageSimple
              messages={messages}
              setMessages={setMessages}
              isError
            />
          )}
        </form>
      }
    />
  )
}
